'use client';

import {
  BADGE_STATUS,
  BadgeStatus,
  BodyDefault,
  Button,
  Field,
  HeadingItem,
  IconButton,
  Input,
  MetaDefault,
  Select,
  Spinner,
  Switch,
  Tooltip,
  toasterApi,
  createSelectListCollection,
  type BadgeStatusType,
} from '@terra/ui-design-system';
import { Box, Divider, Grid, HStack, VStack } from '@terra/ui-styled-system';
import {
  PlayCircleIcon,
  SearchVisualIcon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  useDirectoryScan,
  useDirectoryScans,
  useStartDirectoryScan,
} from '../../hooks/api/useDirectoryScan';
import { useCredentials } from '../../hooks/api/useCredentials';
import { DirectoryScanResults } from './DirectoryScanResults';

const METHOD_OPTIONS = createSelectListCollection({
  items: [
    { label: 'GET', value: 'GET' },
    { label: 'POST', value: 'POST' },
    { label: 'PUT', value: 'PUT' },
    { label: 'DELETE', value: 'DELETE' },
    { label: 'PATCH', value: 'PATCH' },
  ],
});
const SELECT_POSITIONING = {
  strategy: 'fixed',
  placement: 'bottom-start',
  flip: false,
  sameWidth: true,
} as const;
const STATUS_BADGE_BY_STATUS: Record<
  'PENDING' | 'RUNNING' | 'ANALYZING' | 'COMPLETED' | 'FAILED' | 'STOPPED',
  BadgeStatusType
> = {
  PENDING: BADGE_STATUS.NEW,
  RUNNING: BADGE_STATUS.PROGRESS,
  ANALYZING: BADGE_STATUS.PROGRESS,
  COMPLETED: BADGE_STATUS.RESOLVED,
  FAILED: BADGE_STATUS.UNRESOLVED,
  STOPPED: BADGE_STATUS.NEW,
};

export function DirectoryScanPanel({
  applicationId,
  onClose,
}: {
  applicationId: string;
  onClose?: () => void;
}) {
  const t = useTranslations('directoryScan');
  const tCommon = useTranslations('common');
  const [wordlistSize, setWordlistSize] = useState<
    'small' | 'medium' | 'large'
  >('small');
  const [httpMethod, setHttpMethod] = useState<
    'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  >('GET');
  const [startPath, setStartPath] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [credentialId, setCredentialId] = useState<string>();
  const [activeScanId, setActiveScanId] = useState<string>();
  const wordlistOptions = useMemo(
    () =>
      createSelectListCollection({
        items: [
          { label: t('wordlistSizes.small'), value: 'small' },
          { label: t('wordlistSizes.medium'), value: 'medium' },
          { label: t('wordlistSizes.large'), value: 'large' },
        ],
      }),
    [t],
  );
  const { credentials } = useCredentials({ applicationId });
  const credentialOptions = useMemo(
    () =>
      createSelectListCollection({
        items: credentials.map((cred) => ({
          label: cred.username
            ? `${cred.username}${cred.role ? ` (${cred.role})` : ''}`
            : cred.id,
          value: cred.id,
        })),
      }),
    [credentials],
  );
  const listQuery = useDirectoryScans({ applicationId });
  const latestScanId = listQuery.data?.scans[0]?.id;

  useEffect(() => {
    if (activeScanId || !latestScanId) {
      return;
    }

    setActiveScanId(latestScanId);
  }, [activeScanId, latestScanId]);
  const startMutation = useStartDirectoryScan({ applicationId });
  const scanQuery = useDirectoryScan({
    applicationId,
    scanId: activeScanId,
    enabled: !!activeScanId,
  });
  const status = scanQuery.data?.status;
  const isRunning = status === 'RUNNING' || status === 'ANALYZING';
  const hasResults = (scanQuery.data?.results.length ?? 0) > 0;
  const startPathValue = startPath.trim();
  const canRun =
    !startMutation.isPending &&
    !isRunning &&
    (!authenticated || !!credentialId);
  const statusLabel = useMemo(() => {
    if (!status) {
      return null;
    }

    if (status === 'RUNNING') {
      return t('status.running');
    }

    if (status === 'ANALYZING') {
      return t('status.analyzing');
    }

    if (status === 'COMPLETED') {
      return t('status.completed');
    }

    if (status === 'FAILED') {
      return t('status.failed');
    }

    if (status === 'STOPPED') {
      return t('status.stopped');
    }

    return t('status.pending');
  }, [status, t]);
  const handleRunScan = async () => {
    try {
      const response = await startMutation.mutateAsync({
        wordlistSize,
        httpMethod,
        ...(startPathValue ? { startPath: startPathValue } : {}),
        ...(authenticated && credentialId ? { credentialId } : {}),
      });

      setActiveScanId(response.scanId);
      toasterApi.success({ title: t('startSuccessTitle') });
    } catch (error) {
      toasterApi.error({
        title: t('startErrorTitle'),
        description:
          error instanceof Error ? error.message : t('startErrorDescription'),
      });
    }
  };

  return (
    <VStack gap="0" w="full" alignItems="stretch">
      <HStack
        justifyContent="space-between"
        w="full"
        px="3"
        py="3"
        gap="2"
        borderBottomWidth="1px"
        borderColor="border.muted"
      >
        <HStack gap="2" alignItems="center">
          <SearchVisualIcon size={18} />
          <HeadingItem>{t('title')}</HeadingItem>
        </HStack>

        <HStack gap="2">
          <Button
            size="xs"
            variant="outline"
            onClick={handleRunScan}
            disabled={!canRun}
          >
            {startMutation.isPending || isRunning ? (
              <HStack gap="2">
                <Spinner size="xs" />
                <span>{statusLabel ?? t('run')}</span>
              </HStack>
            ) : (
              <HStack gap="2">
                <PlayCircleIcon size={14} />
                <span>{t('run')}</span>
              </HStack>
            )}
          </Button>
          {onClose ? (
            <Tooltip
              title={tCommon('close')}
              positioning={{ strategy: 'fixed' }}
            >
              <IconButton
                variant="ghost"
                size="xs"
                onClick={onClose}
                aria-label={tCommon('close')}
              >
                <Cancel01Icon size={16} />
              </IconButton>
            </Tooltip>
          ) : null}
        </HStack>
      </HStack>

      <VStack gap="3" w="full" px="3" py="3">
        <Grid w="full" gap="2" gridTemplateColumns="1fr 1fr">
          <Field.Root>
            <Field.Label fontSize="xs" fontWeight="medium" color="fg.muted">
              {t('wordlistSize')}
            </Field.Label>
            <Select.Root
              collection={wordlistOptions}
              value={[wordlistSize]}
              onValueChange={(details) =>
                setWordlistSize(
                  (details.value[0] ?? 'small') as typeof wordlistSize,
                )
              }
              positioning={SELECT_POSITIONING}
              size="sm"
              disabled={isRunning}
            >
              <Select.HiddenSelect />
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  {wordlistOptions.items.map((item) => (
                    <Select.Item item={item} key={item.value}>
                      {item.label}
                      <Select.ItemIndicator />
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </Field.Root>

          <Field.Root>
            <Field.Label fontSize="xs" fontWeight="medium" color="fg.muted">
              {t('httpMethod')}
            </Field.Label>
            <Select.Root
              collection={METHOD_OPTIONS}
              value={[httpMethod]}
              onValueChange={(details) =>
                setHttpMethod((details.value[0] ?? 'GET') as typeof httpMethod)
              }
              positioning={SELECT_POSITIONING}
              size="sm"
              disabled={isRunning}
            >
              <Select.HiddenSelect />
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  {METHOD_OPTIONS.items.map((item) => (
                    <Select.Item item={item} key={item.value}>
                      {item.label}
                      <Select.ItemIndicator />
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </Field.Root>
        </Grid>

        <Field.Root>
          <Field.Label fontSize="xs" fontWeight="medium" color="fg.muted">
            {t('startPath')}
          </Field.Label>
          <Input
            size="sm"
            value={startPath}
            onChange={(event) => setStartPath(event.target.value)}
            placeholder={t('startPathPlaceholder')}
            disabled={isRunning}
          />
          <Field.HelperText fontSize="xs">
            {t('startPathHelper')}
          </Field.HelperText>
        </Field.Root>

        <VStack gap="2" w="full" alignItems="stretch">
          <Switch
            size="sm"
            checked={authenticated}
            disabled={isRunning}
            onCheckedChange={(details) => {
              setAuthenticated(details.checked);
              if (!details.checked) {
                setCredentialId(undefined);
              }
            }}
          >
            {t('authenticated')}
          </Switch>

          {authenticated && credentialOptions.items.length > 0 ? (
            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="medium" color="fg.muted">
                {t('credential')}
              </Field.Label>
              <Select.Root
                collection={credentialOptions}
                value={credentialId ? [credentialId] : []}
                onValueChange={(details) =>
                  setCredentialId(details.value[0] ?? undefined)
                }
                positioning={SELECT_POSITIONING}
                size="sm"
                disabled={isRunning}
              >
                <Select.HiddenSelect />
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText
                      placeholder={t('credentialPlaceholder')}
                    />
                  </Select.Trigger>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content>
                    {credentialOptions.items.map((item) => (
                      <Select.Item item={item} key={item.value}>
                        {item.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            </Field.Root>
          ) : null}

          {authenticated && credentialOptions.items.length === 0 ? (
            <BodyDefault color="fg.muted">{t('noCredentials')}</BodyDefault>
          ) : null}
        </VStack>

        {statusLabel && status && !isRunning ? (
          <HStack w="full" justifyContent="space-between">
            <MetaDefault color="fg.muted">{t('statusLabel')}</MetaDefault>
            <BadgeStatus status={STATUS_BADGE_BY_STATUS[status]} size="sm">
              {statusLabel}
            </BadgeStatus>
          </HStack>
        ) : null}

        {status !== 'STOPPED' && scanQuery.data?.error ? (
          <BodyDefault color="fg.error">{scanQuery.data.error}</BodyDefault>
        ) : null}

        {hasResults && scanQuery.data ? (
          <>
            <Divider />
            <DirectoryScanResults results={scanQuery.data.results} />
          </>
        ) : null}

        {scanQuery.isFetching && !scanQuery.data ? (
          <Box py="3" display="flex" justifyContent="center">
            <Spinner size="sm" />
          </Box>
        ) : null}
      </VStack>
    </VStack>
  );
}
